'use client';

import { 
  ControlType, 
  CalculatorId, 
  CalculatorType, 
  ControlIds, 
  type CalculatorConfig, 
  type ControlValues,
  type MultiColumnSelectControl,
  type TextControl,
  type SelectControl,
  type NumberControl,
  type SingleColumnSelectControl
} from '../../../lib/calculators/calculator-config';
import { TableDataJSON } from '../../../src/types/table-json';
import { LazyTabLabECharts } from '@repo/ui';
import type { EChartsOption } from 'echarts';

// Type for sankey specific control values
export interface SankeyControlValues extends ControlValues {
  [ControlIds.Category]: string;
  [ControlIds.Value]: string;
  [ControlIds.Title]: string;
  [ControlIds.ShowLabels]: string;
  [ControlIds.Orientation]: string;
}

// Define the controls for the sankey chart
export const sankeyControls = {
  tabs: [
    {
      title: 'Data',
      controls: [
        {
          id: ControlIds.Category,
          type: ControlType.SingleColumnSelect,
          label: 'Flow path column',
          required: true
        } satisfies SingleColumnSelectControl,
        {
          id: ControlIds.Value,
          type: ControlType.SingleColumnSelect,
          label: 'Value column',
          required: true,
          numericOnly: true
        } satisfies SingleColumnSelectControl,
        {
          id: ControlIds.Title,
          type: ControlType.Text,
          label: 'Chart title',
          required: false
        } satisfies TextControl
      ]
    },
    {
      title: 'Appearance',
      controls: [
        {
          id: ControlIds.Orientation,
          type: ControlType.Select,
          label: 'Orientation',
          options: [
            { label: 'Horizontal', value: 'horizontal' },
            { label: 'Vertical', value: 'vertical' }
          ],
          defaultValue: 'horizontal'
        } satisfies SelectControl,
        {
          id: ControlIds.ShowLabels,
          type: ControlType.Select,
          label: 'Show labels',
          options: [
            { label: 'Yes', value: 'true' },
            { label: 'No', value: 'false' }
          ],
          defaultValue: 'true'
        } satisfies SelectControl
      ]
    }
  ]
};

// Helper function to build sankey data from flat data
function buildSankeyData(
  paths: string[],
  values: number[]
): { nodes: { name: string }[]; links: { source: string; target: string; value: number }[] } {
  const nodes = new Set<string>();
  const links: { source: string; target: string; value: number }[] = [];

  paths.forEach((path, index) => {
    const parts = path.split('->').map(p => p.trim()).filter(Boolean);
    if (parts.length < 2) return; // Skip invalid paths
    
    // Add all nodes
    parts.forEach(part => nodes.add(part));
    
    // Create links between consecutive nodes
    for (let i = 0; i < parts.length - 1; i++) {
      const source = parts[i];
      const target = parts[i + 1];
      const value = values[index];
      
      if (!source || !target || typeof value !== 'number') continue;
      
      // Find existing link or create new one
      const existingLink = links.find(link => 
        link.source === source && link.target === target
      );
      
      if (existingLink) {
        existingLink.value += value;
      } else {
        links.push({ source, target, value });
      }
    }
  });

  return {
    nodes: Array.from(nodes).map(name => ({ name })),
    links
  };
}

// Pure function to generate ECharts config for a sankey chart
export function getSankeyEChartsConfig({
  data,
  title,
  orientation,
  showLabels
}: {
  data: { nodes: { name: string }[]; links: { source: string; target: string; value: number }[] };
  title?: string;
  orientation: 'horizontal' | 'vertical';
  showLabels: boolean;
}): EChartsOption {
  return {
    title: {
      text: title || 'Sankey Diagram',
      left: 'center',
      top: 2,
      padding: [0, 0, 0, 0]
    },
    tooltip: {
      trigger: 'item',
      triggerOn: 'mousemove',
      formatter: (params: any) => {
        if (params.dataType === 'node') {
          return `${params.name}`;
        }
        const link = params.data;
        return link ? `${link.source} → ${link.target}: ${link.value.toFixed(2)}` : '';
      }
    },
    series: [
      {
        type: 'sankey',
        orient: orientation,
        emphasis: {
          focus: 'adjacency'
        },
        nodeAlign: 'justify',
        data: data.nodes,
        links: data.links,
        label: {
          show: showLabels,
          position: orientation === 'horizontal' ? 'right' : 'top',
          fontSize: 12
        },
        lineStyle: {
          color: 'source',
          curveness: 0.5
        },
        itemStyle: {
          borderWidth: 1,
          borderColor: '#fff'
        },
        animation: true
      }
    ]
  };
}

export const sankeyConfig: CalculatorConfig<CalculatorId.SankeyChart> = {
  id: CalculatorId.SankeyChart,
  type: CalculatorType.Graph,
  name: 'Sankey Diagram',
  initialData: TableDataJSON.from({
    columns: [
      { 
        id: 'flow', 
        name: 'Flow', 
        values: [
          'Revenue->Operating Costs->Salaries',
          'Revenue->Operating Costs->Supplies',
          'Revenue->Taxes->Federal',
          'Revenue->Taxes->State',
          'Revenue->Profit->Dividends',
          'Revenue->Profit->Reinvestment',
          'Revenue->Marketing->Digital',
          'Revenue->Marketing->Traditional'
        ] 
      },
      { 
        id: 'amount', 
        name: 'Amount ($K)', 
        values: ['500', '300', '200', '150', '250', '300', '180', '120'] 
      }
    ]
  }),
  initialControlValues: {
    [ControlIds.Category]: 'flow',
    [ControlIds.Value]: 'amount',
    [ControlIds.Title]: 'Revenue Flow Analysis',
    [ControlIds.Orientation]: 'horizontal',
    [ControlIds.ShowLabels]: 'true'
  },
  controls: sankeyControls,
  getInitialConfig: (data: TableDataJSON) => {
    const numericColumns = data.columns.filter(col => 
      col.values.every(val => !isNaN(Number(val)))
    );
    const nonNumericColumns = data.columns.filter(col => 
      !col.values.every(val => !isNaN(Number(val)))
    );

    return {
      [ControlIds.Category]: nonNumericColumns[0]?.id || data.columns[0]?.id,
      [ControlIds.Value]: numericColumns[0]?.id,
      [ControlIds.Title]: 'Sankey Diagram',
      [ControlIds.Orientation]: 'horizontal',
      [ControlIds.ShowLabels]: 'true'
    };
  },
  getVisualizationData: ({ data, controls }) => {
    const sankeyControls = controls as SankeyControlValues;
    
    if (!data?.columns?.length) {
      return null;
    }

    const categoryColumn = data.columns.find(col => col.id === sankeyControls[ControlIds.Category]);
    const valueColumn = data.columns.find(col => col.id === sankeyControls[ControlIds.Value]);

    if (!categoryColumn || !valueColumn) {
      return null;
    }

    const sankeyData = buildSankeyData(
      categoryColumn.values,
      valueColumn.values.map(v => Number(v))
    );

    return getSankeyEChartsConfig({
      data: sankeyData,
      title: sankeyControls[ControlIds.Title],
      orientation: sankeyControls[ControlIds.Orientation] as 'horizontal' | 'vertical',
      showLabels: sankeyControls[ControlIds.ShowLabels] === 'true'
    });
  },
  render: ({ visualizationData }) => 
    visualizationData ? (
      <LazyTabLabECharts option={visualizationData} className="h-full w-full" />
    ) : null
}; 